import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "./Header";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import Role from './Role';
import { BASE_URL } from "../Apis/apiConfig";
import { Users, Home, Calendar, Shield, AlertCircle, Loader} from "lucide-react";
import "../Style/Dashboard.css";
import "../Style/MemberTable.css";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Edit, Trash2 } from "lucide-react";



const MemberTable = () => {
  const navigate = useNavigate();
  const { role } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [membersData, setMembersData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleEditClick = (member) => {
    setSelectedMember(member);
    setEditData({ ...member });
    setIsModalOpen(true);
  };


  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setEditData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };



  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem("token");
      if (!token) {
        if (!cancelled) {
          setError("Not authenticated. Please log in.");
          setMembersData([]);
          setLoading(false);
        }
        return;
      }

      try {
        const baseUrl = `${BASE_URL}/api/assign-role/get-assign-role`;
        const decodedRole = role ? decodeURIComponent(role) : null;
        const societyName = localStorage.getItem("society") || "";
        const params = new URLSearchParams();
        if (decodedRole) params.append("designation", decodedRole);
        if (societyName) params.append("societyName", societyName);

        const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

        const resp = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (resp.status === 401) {
          if (!cancelled) {
            setError("Unauthorized — please log in again.");
            setMembersData([]);
            setLoading(false);
          }
          return;
        }

        if (!resp.ok) {
          const txt = await resp.text().catch(() => null);
          throw new Error(`Server returned ${resp.status}: ${txt || resp.statusText}`);
        }

        const data = await resp.json();

        if (!cancelled) {
          if (data == null) {
            setMembersData([]);
          } else if (Array.isArray(data)) {
            setMembersData(data);
          } else if (data.items && Array.isArray(data.items)) {
            setMembersData(data.items);
          } else {
            setMembersData([data]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Fetch members failed:", err);
          setError(err.message || "Failed to fetch members");
          setMembersData([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [role]);

  const handleDelete = async (memberId) => {
    try {
      const token = localStorage.getItem("token");
      const societyName = localStorage.getItem("society");

      const response = await axios.delete(
        `${BASE_URL}/api/assign-role/delete-assign-role?assignRoleId=${memberId}&societyName=${encodeURIComponent(societyName)}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Delete success:", response.data);


      setMembersData((prev) =>
        prev.filter((member) => member.id !== memberId)
      );

    } catch (error) {
      console.error(
        "Delete failed:",
        error.response?.data || error.message
      );
    }
  };

const handleUpdate = async () => {
  try {
    const token = localStorage.getItem("token");

    await axios.post(
      `${BASE_URL}/api/assign-role/edit-assign-role`,
      null, // Body is null because backend expects query params
      {
        params: {
          roleAssignRoleId: editData.assignRoleId,
          societyName: editData.societyName,
          name: editData.memberName,
          designation: editData.designation,
          unitNo: editData.unitNo,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    toast.success("Member updated successfully");

    // Update the local state
    setMembersData((prev) =>
      prev.map((m) =>
        m.assignRoleId === editData.assignRoleId
          ? { ...m, ...editData }
          : m
      )
    );

    setIsModalOpen(false);
  } catch (error) {
    toast.error("Update failed");
    console.error(error);
  }
};


  return (
    <div className="app-container">
      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
        <main className="content-area">
          <SubHeader />

          <div className="member-table-page">
            {/* Left Sidebar - Roles */}
            <aside className="member-roles-sidebar">
              <div className="member-roles-header">
                <Shield size={18} />
                <span>Roles</span>
              </div>
              <div className="member-roles-list">
                <Role />
              </div>
            </aside>

            {/* Main Content - Member Table */}
            <section className="member-main-content">
              <div className="member-content-header">
                <div className="member-title-section">
                  <Shield size={22} />
                  <h1>Members</h1>
                  {role && (
                    <span className="role-badge-modern">
                      {decodeURIComponent(role)}
                    </span>
                  )}
                </div>
              </div>

              <div className="member-table-wrapper">
                {loading ? (
                  <div className="member-state loading">
                    <Loader size={32} className="spinner" />
                    <p>Loading members...</p>
                  </div>
                ) : error ? (
                  <div className="member-state error">
                    <AlertCircle size={32} />
                    <p>{error}</p>
                  </div>
                ) : membersData.length === 0 ? (
                  <div className="member-state empty">
                    <Users size={48} />
                    <p>No members found for this role</p>
                  </div>
                ) : (
                  <div className="member-table-container">
                    <table className="member-table">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Wing / Flat</th>
                          <th>From Date</th>
                          <th>Till Date</th>
                          <th>Status</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {membersData.map((member, index) => (
                          <tr key={index}>
                            <td>
                              <div className="member-name-cell">
                                <div className="member-avatar">
                                  {member.memberName
                                    ? member.memberName.charAt(0).toUpperCase()
                                    : "U"}
                                </div>
                                <span className="member-name">
                                  {member.memberName || "—"}
                                </span>
                              </div>
                            </td>

                            <td>
                              <div className="member-info-cell">
                                <Home size={14} />
                                <span>{member.unitNo || "—"}</span>
                              </div>
                            </td>

                            <td>
                              <div className="member-info-cell">
                                <Calendar size={14} />
                                <span>{member.startDate || "—"}</span>
                              </div>
                            </td>

                            <td>{member.till || "—"}</td>

                            <td>
                              {member.status === "Resign" ? (
                                <span className="member-status-badge resign">Resign</span>
                              ) : (
                                <span className="member-status-badge previous">Working</span>
                              )}
                            </td>
                            <td className="action-icons">
                              <Edit
                                size={18}
                                className="edit-icon"
                                onClick={() => handleEditClick(member)}
                              />

                              <Trash2
                                size={18}
                                className="delete-icon"
                                onClick={() => handleDelete(member.assignRoleId)}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {isModalOpen && editData && (
                      <div className="modal-overlay">
                        <div className="modal-container modern">

                          {/* Header */}
                          <div className="modal-header">
                            <h2>Edit Member</h2>
                            <button className="close-btn" onClick={() => setIsModalOpen(false)}>×</button>
                          </div>

                          {/* Body */}
                          <div className="modal-body">

                            <div className="form-grid">

                              <div className="form-group">
                                <label>Name</label>
                                <input
                                  type="text"
                                  name="memberName"
                                  value={editData.memberName || ""}
                                  onChange={handleChange}
                                />
                              </div>

                              <div className="form-group">
                                <label>Designation</label>
                                <input
                                  type="text"
                                  value={editData.designation || ""}
                                  disabled
                                />
                              </div>

                              <div className="form-group">
                                <label>Society</label>
                                <input
                                  type="text"
                                  value={editData.societyName || ""}
                                  disabled
                                />
                              </div>

                              <div className="form-group">
                                <label>Unit No</label>
                                <input
                                  type="text"
                                  name="unitNo"
                                  value={editData.unitNo || ""}
                                  onChange={handleChange}
                                />
                              </div>

                              <div className="form-group">
                                <label>Start Date</label>
                                <input
                                  type="date"
                                  name="startDate"
                                  value={editData.startDate || ""}
                                  onChange={handleChange}
                                />
                              </div>

                            </div>

                            {/* Toggles */}
                            <div className="toggle-group">
                              <label className="toggle">
                                <input
                                  type="checkbox"
                                  name="currentWork"
                                  checked={editData.currentWork || false}
                                  onChange={handleChange}
                                />
                                <span className="slider"></span>
                                Currently Working
                              </label>

                              <label className="toggle">
                                <input
                                  type="checkbox"
                                  name="agree"
                                  checked={editData.agree || false}
                                  onChange={handleChange}
                                />
                                <span className="slider"></span>
                                Agreement Accepted
                              </label>
                            </div>

                          </div>

                          {/* Footer */}
                          <div className="modal-footer">
                            <button className="btn secondary" onClick={() => setIsModalOpen(false)}>
                              Cancel
                            </button>
                            <button className="btn primary" onClick={handleUpdate}>
                              Save Changes
                            </button>
                          </div>

                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MemberTable;
